import { ConditionalSkillStep, SkillStep } from "@/core/skillForms/types";
import { removeUndefinedValues } from "@/utils/removeUndefinedValues";
import { UiSchema } from "@rjsf/utils";
import { FileTypes } from "../skills/types";

type BuildSkillStepFieldOption<OptionValue extends string | null = string | null> = {
  value: OptionValue;
  label?: string;
  description?: string;
};

type BuildSkillStepUiOptions<OptionValue extends string | null = string | null> = {
  enumDisabled?: (OptionValue | null)[];
  multiple?: boolean;
  accept?: FileTypes[];
};

type BuildSkillStepUi<OptionValue extends string | null = string | null> = {
  label?: string | null;
  widget?: "input" | "textarea" | "radio" | "selectText";
  field?: string;
  placeholder?: string;
  description?: string;
  enableMarkdownInDescription?: boolean;
  options?: BuildSkillStepUiOptions<OptionValue>;
  hidden?: boolean;
};

type BuildSkillStepCondition<Name extends string = string, OptionValue extends string = string> = {
  field: Name;
  value: OptionValue;
};

type BuildSkillStepFieldType = "string" | "boolean" | "editor_content" | "file";

type BaseBuildSkillStepField<Name extends string = string, OptionValue extends string = string> = {
  name: Name;
  overrideField?: Name;
  type: BuildSkillStepFieldType;
  initialValue?: OptionValue | null;
  ui: BuildSkillStepUi;
  condition?: BuildSkillStepCondition<Name, OptionValue>;
  minLength?: number;
  maxLength?: number;
  onboarding?: {
    enabledOptions?: OptionValue[];
  };
};

type BuildSkillStepField<Name extends string = string, OptionValue extends string = string> =
  | (BaseBuildSkillStepField<Name, OptionValue> & {
      required?: true | undefined;
      options?: BuildSkillStepFieldOption<OptionValue>[];
      hidden?: boolean;
    })
  | (BaseBuildSkillStepField<Name, OptionValue> & {
      required: false;
      options?: BuildSkillStepFieldOption<OptionValue | null>[];
      hidden?: boolean;
    });

type BuildSkillStepOptions<Name extends string, OptionValue extends string> = {
  title?: string;
  description?: string;
  fields: BuildSkillStepField<Name, OptionValue>[];
  submitButtonText?: string;
  onboarding?: {
    templateButton: string;
  };
};

type BuildConditionalSkillStepOptions<Name extends string, OptionValue extends string> = BuildSkillStepOptions<
  Name,
  OptionValue
> & {
  condition: BuildSkillStepCondition<Name, OptionValue> | Array<BuildSkillStepCondition<Name, OptionValue>>;
};

const optionToProperty = (option: BuildSkillStepFieldOption) => {
  return removeUndefinedValues({
    type: option.value ? "string" : "null",
    enum: [option.value],
    title: option.label ?? option.value,
    description: option.description,
  });
};

const fieldToPropertyName = ({ field }: { field: BuildSkillStepField }) =>
  "overrideField" in field ? `override:${field.type}:${field.overrideField}` : field.name;

const fieldToProperty = ({ field }: { field: BuildSkillStepField }) => {
  if (field.options) {
    return {
      ...fieldToPropertyType({ field }),
      anyOf: field.options.map(optionToProperty),
      uniqueItems: true,
    };
  }
  return fieldToPropertyType({ field });
};

const fieldToPropertyType = ({ field }: { field: BuildSkillStepField }) => {
  const isRequired = isFieldRequired({ field });
  switch (field.type) {
    case "string":
    case "editor_content":
      return {
        type: isRequired ? "string" : ["null", "string"],
        minLength: field.minLength ?? (isRequired ? 1 : 0),
        maxLength: field.maxLength,
      };
    case "boolean":
      return {
        type: isRequired ? "boolean" : ["null", "boolean"],
      };
    case "file":
      return field.ui.options?.multiple
        ? {
            type: isRequired ? "array" : ["null", "array"],
            items: {
              type: "string",
              format: "data-url",
            },
          }
        : {
            type: isRequired ? "string" : ["null", "string"],
            format: "data-url",
          };
    default:
      return {};
  }
};

const fieldToCondition = ({ field }: { field: BuildSkillStepField }) => {
  if (field.condition) {
    const fieldName = fieldToPropertyName({ field });
    const fieldProperty = fieldToProperty({ field });

    return {
      fieldName,
      condition: {
        if: {
          properties: {
            [field.condition.field]: {
              const: field.condition.value,
            },
          },
        },
        then: {
          properties: {
            [fieldName]: fieldProperty,
          },
          required: [fieldName],
        },
      },
      fieldUi: field.ui,
    };
  }

  return undefined;
};

const buildSkillStepUiToUiSchema = (ui?: BuildSkillStepUi): UiSchema => {
  return ui
    ? removeUndefinedValues({
        "ui:title": ui.label ?? undefined,
        "ui:placeholder": ui.placeholder,
        "ui:widget": ui.widget,
        "ui:field": ui.field,
        "ui:label": ui.label !== null,
        "ui:description": ui.description,
        "ui:options": ui.options,
        "ui:enableMarkdownInDescription": ui.enableMarkdownInDescription,
      })
    : {};
};

const isFieldRequired = ({ field }: { field: BuildSkillStepField }) => {
  return field.required ?? true;
};

function buildSkillStep<Name extends string, OptionValue extends string>(
  props: BuildSkillStepOptions<Name, OptionValue>
): SkillStep;
// eslint-disable-next-line no-redeclare
function buildSkillStep<Name extends string, OptionValue extends string>(
  props: BuildConditionalSkillStepOptions<Name, OptionValue>
): ConditionalSkillStep;
// eslint-disable-next-line no-redeclare
function buildSkillStep<Name extends string, OptionValue extends string>(
  props: BuildSkillStepOptions<Name, OptionValue> | BuildConditionalSkillStepOptions<Name, OptionValue>
): SkillStep | ConditionalSkillStep {
  const { title, description, onboarding, fields, submitButtonText } = props;

  const baseStep: SkillStep = {
    title,
    schema: removeUndefinedValues({
      description,
      onboarding,
      type: "object",
      properties: {},
      required: [],
      allOf: [],
    }),
    uiSchema: {
      "ui:enableMarkdownInDescription": true,
      "ui:order": fields.map((field) => fieldToPropertyName({ field })),
    },
    submitButtonText,
  };
  return fields.reduce(
    (acc, field) => {
      const isRequired = isFieldRequired({ field });
      const propertyName = fieldToPropertyName({ field });
      const property = fieldToProperty({ field });
      const condition = fieldToCondition({ field });

      const schema = {
        ...acc.schema,
        properties: condition
          ? {
              ...acc.schema.properties,
            }
          : {
              ...acc.schema.properties,
              [propertyName]: property,
            },
        required: condition
          ? acc.schema.required
          : isRequired
            ? [...(acc.schema.required ?? []), propertyName]
            : acc.schema.required,
        allOf: condition ? [...(acc.schema.allOf ?? []), condition.condition] : acc.schema.allOf,
      };

      const fieldUi = (() => {
        let ui = field.ui || {};
        if (field.onboarding?.enabledOptions && field.options) {
          const disabledOptions = field.options
            .filter(
              (option) =>
                option.value !== null && !field.onboarding!.enabledOptions!.includes(option.value as OptionValue)
            )
            .map((option) => option.value);

          ui = {
            ...ui,
            options: {
              ...(ui.options || {}),
              enumDisabled: disabledOptions,
            },
          };
        }
        return ui;
      })();

      const uiSchema = condition
        ? {
            ...acc.uiSchema,
            [condition.fieldName]: buildSkillStepUiToUiSchema(condition.fieldUi),
          }
        : {
            ...acc.uiSchema,
            [propertyName]: buildSkillStepUiToUiSchema(fieldUi),
          };

      return {
        ...acc,
        schema,
        uiSchema,
        initialData: field.initialValue
          ? {
              ...acc.initialData,
              [propertyName]: field.initialValue,
            }
          : acc.initialData,
      } as SkillStep;
    },
    "condition" in props
      ? {
          ...baseStep,
          condition: props.condition,
        }
      : baseStep
  );
}

export const createEditorContentField = <Name extends string = string, OptionValue extends string = string>(
  field: Omit<BuildSkillStepField<Name, OptionValue>, "type" | "ui" | "initialValue" | "options"> & {
    ui?: BuildSkillStepUi;
    hidden?: boolean;
  }
) => {
  return {
    ...field,
    type: "editor_content",
    hidden: field.hidden ?? false,
    ui: {
      ...field.ui,
      label: null,
      field: "EditorContentField",
    },
  } as BuildSkillStepField<Name, OptionValue>;
};

export const createSelectTextField = <Name extends string = string, OptionValue extends string = string>(
  field: Omit<BuildSkillStepField<Name, OptionValue>, "type" | "ui" | "initialValue" | "options"> & {
    ui?: BuildSkillStepUi;
  }
) => {
  return {
    ...field,
    type: "string",
    ui: {
      ...field.ui,
      field: "SelectTextField",
    },
  } as BuildSkillStepField<Name, OptionValue>;
};

export const createUploadFileField = <Name extends string = string, OptionValue extends string = string>(
  field: Omit<BuildSkillStepField<Name, OptionValue>, "type" | "ui" | "initialValue" | "options"> & {
    ui?: BuildSkillStepUi;
  }
) => {
  return {
    ...field,
    type: "file",
    ui: field.ui ?? {
      label: null,
    },
  } as BuildSkillStepField<Name, OptionValue>;
};

export { buildSkillStep };
